/** Batch "Set expiration" dialog for the API-credentials grid (a custom
 *  `dialog(ctx)` batch action) — set one expiration datetime on the selected
 *  credentials, or clear it. A past datetime is a valid soft-expiry (the keys
 *  stop authenticating while keeping their status).
 */
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormHelperText,
} from '@mui/material';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFnsV3';
import { useState } from 'react';

import type { BatchActionContext } from '@/components/DataGrid/batchTypes';

export function SetExpirationBatchDialog({ ctx }: { ctx: BatchActionContext }) {
  const [expires, setExpires] = useState<Date | null>(null);
  const [clear, setClear] = useState(false);

  const valid = clear || expires !== null;
  const inPast = !clear && expires !== null && expires.getTime() < Date.now();

  const submit = () => {
    void ctx.runStandard({ expires: clear ? null : expires!.toISOString() });
    ctx.close();
  };

  return (
    <Dialog open onClose={ctx.close} fullWidth maxWidth="sm">
      <DialogTitle>Set credential expiration</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DateTimePicker
              label="Expires (UTC)"
              value={expires}
              onChange={setExpires}
              disabled={clear}
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
          </LocalizationProvider>
          <Box>
            <FormControlLabel
              control={
                <Checkbox size="small" checked={clear} onChange={(e) => setClear(e.target.checked)} />
              }
              label="Clear expiration (keys never expire)"
            />
            <FormHelperText sx={{ mt: -0.5 }}>
              Expiry is a soft cutoff: expired keys stop authenticating but keep their status.
            </FormHelperText>
          </Box>

          {inPast && (
            <Alert severity="warning">
              The chosen time is in the past — these keys stop authenticating immediately.
            </Alert>
          )}

          {ctx.selection.mode === 'all_matching' && (
            <Alert severity="info">Acting on {ctx.matchedPhrase} credentials.</Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={ctx.close}>Cancel</Button>
        <Button variant="contained" disabled={!valid} onClick={submit}>
          {clear ? 'Clear' : 'Set'} expiration for {ctx.count.toLocaleString()} credential(s)
        </Button>
      </DialogActions>
    </Dialog>
  );
}
